import Bugsnag, { type Event } from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import type { App } from 'vue'

import type { CurrentUser } from '@/javascript/interfaces'

const bugsnagApiKey = import.meta.env.BUGSNAG_JS_API_KEY

// Hosts whose requests flood breadcrumb trails (Wistia video segments, New
// Relic beacons) and bury the requests that help diagnose a crash. Add a host
// here when a new vendor's requests start dominating breadcrumb trails.
export const NOISY_BREADCRUMB_REQUEST_HOSTS = [
	'bam.nr-data.net',
	'distillery.wistia.com',
	'embed-cloudfront.wistia.com',
	'pipedream.wistia.com',
]

// Hosts of vendor scripts whose internal errors we can't act on. Add a host
// here when a new hosted vendor script generates non-actionable noise.
export const THIRD_PARTY_SCRIPT_HOSTS = [
	'fast.appcues.com',
	'px.ads.linkedin.com',
	'snap.licdn.com',
	'www.google-analytics.com',
	'www.googletagmanager.com',
]

/**
 * Determines whether an error message comes from a script Brave for iOS
 * injects, which namespaces them under window.__firefox__.
 * @param errorMessage - The reported error's message
 * @returns true when the message references the injected-script namespace
 */
export const isBraveIosInjectedScriptError = (errorMessage: string): boolean =>
	errorMessage.includes('__firefox__')

/**
 * Determines whether a non-Error payload is the signature of CefSharp, the
 * engine Outlook's "Safe Links" bot uses to prescan links in emails.
 * @param nonErrorPayload - The value from Bugsnag's "non-error parameter" metadata
 * @returns true when the payload is CefSharp's bot-crawler signature
 */
export const isCefSharpBotError = (nonErrorPayload: unknown): boolean =>
	typeof nonErrorPayload === 'string' &&
	nonErrorPayload.startsWith('Object Not Found Matching Id')

/**
 * Determines whether an error message is the browser's notice for an eval()
 * our CSP blocked. The call site is always a blob: URL on our own origin and
 * the message embeds a per-request nonce, so it can't be matched by host or
 * grouped. CspViolationReportsController already captures these server-side.
 * @param errorMessage - The reported error's message
 * @returns true when the message is a CSP eval-violation notice
 */
export const isCspEvalViolationError = (errorMessage: string): boolean =>
	errorMessage.includes('Evaluating a string as JavaScript violates the following Content Security Policy directive')

/**
 * Determines whether a non-Error payload is a plain object with no own keys.
 * @param nonErrorPayload - The value from Bugsnag's "non-error parameter" metadata
 * @returns true when the payload is a plain object with no own keys
 */
export const isEmptyNonErrorPayload = (nonErrorPayload: unknown): boolean =>
	typeof nonErrorPayload === 'object' &&
	nonErrorPayload !== null &&
	Object.keys(nonErrorPayload).length === 0

/**
 * Determines whether an error message is the browser's generic fetch() failure.
 * Our own fetch() callers must catch their failures, so an unhandled one comes
 * from a script a browser extension injected, whose requests our CSP
 * connect-src blocks.
 * @param errorMessage - The reported error's message
 * @returns true when the message is exactly the generic fetch failure
 */
export const isFailedFetchError = (errorMessage: string): boolean =>
	/^(Unhandled Promise Rejection: )?Failed to fetch$/.test(errorMessage)

/**
 * Determines whether a stack trace points at the HTML document rather than at
 * a JavaScript file, as inline and injected scripts do. Frames with no file are
 * allowed: browser-injected scripts report no file, and Bugsnag fills in the
 * top frame from the page URL.
 * InvalidError is excluded: Bugsnag reports the page URL for every non-Error
 * value, and its default grouping already separates those by page.
 * @param stacktrace - Stack frames from the reported error
 * @param errorClass - The reported error's class name
 * @returns true when at least one frame has a file and none comes from a
 * JavaScript file
 */
export const isInlineDocumentError = (
	stacktrace: Array<{ file?: string }>,
	errorClass?: string
): boolean => {
	if (errorClass === 'InvalidError') {
		return false
	}

	const files = stacktrace.map(frame => (frame.file ?? '').split(/[?#]/)[0])

	return files.some(file => file.length > 0) && files.every(file => !file.endsWith('.js'))
}

/**
 * Determines whether an error message is the browser's benign ResizeObserver
 * loop notification.
 * @param errorMessage - The reported error's message
 * @returns true when the message is a ResizeObserver loop notification
 */
export const isResizeObserverLoopError = (errorMessage: string): boolean =>
	errorMessage.startsWith('ResizeObserver loop')

/**
 * Determines whether an error's stack trace originates in a known third-party
 * vendor script.
 * @param stacktrace - Stack frames from the reported error
 * @returns true when any frame belongs to a denylisted vendor host
 */
export const isThirdPartyScriptError = (
	stacktrace: Array<{ file?: string }>
): boolean =>
	stacktrace.some(frame =>
		THIRD_PARTY_SCRIPT_HOSTS.some(host => frame.file?.includes(host))
	)

/**
 * Determines whether an error's stack trace includes a script we don't serve
 * and can't trace, such as a Chrome extension.
 * @param stacktrace - Stack frames from the reported error
 * @returns true when any frame's file is unknown or a Chrome extension
 */
export const isUntraceableScriptError = (
	stacktrace: Array<{ file?: string }>
): boolean =>
	stacktrace.some(frame =>
		frame.file === 'unknown' || frame.file?.startsWith('chrome-extension://')
	)

/**
 * Determines whether an error is Wistia's empty rejection for a video that
 * failed to decode. VideoPlayer.tsx shows that failure to the user directly.
 * @param errorClass - The reported error's class name
 * @param context - The event's context (the page path)
 * @param nonErrorPayload - The value from Bugsnag's "non-error parameter" metadata
 * @returns true when the error is an empty InvalidError on the video library
 */
export const isVideoLibraryDecodeError = (
	errorClass: string | undefined,
	context: string | undefined,
	nonErrorPayload: unknown
): boolean =>
	errorClass === 'InvalidError' &&
	(context?.includes('video_library') ?? false) &&
	isEmptyNonErrorPayload(nonErrorPayload)

/**
 * Determines whether an error message comes from the Zendesk widget, whose
 * "InboundFilters" identifier our code never uses. Remove this filter once the
 * Zendesk widget is removed.
 * @param errorMessage - The reported error's message
 * @returns true when the message references InboundFilters
 */
export const isZendeskWidgetError = (errorMessage: string): boolean =>
	errorMessage.includes('InboundFilters')

/**
 * Determines whether a breadcrumb is a request to a known noisy vendor host.
 * @param breadcrumb - The breadcrumb Bugsnag is about to record
 * @returns true when the breadcrumb is a request to a denylisted noisy host
 */
export const isNoisyRequestBreadcrumb = (
	breadcrumb: { metadata?: { url?: string }; type?: string }
): boolean =>
	breadcrumb.type === 'request' &&
	NOISY_BREADCRUMB_REQUEST_HOSTS.some(host => breadcrumb.metadata?.url?.includes(host))

// Bugsnag's unhandledrejection listener reports before createVueApp.ts's own
// listener runs, so Event#preventDefault() can't suppress it. This flag is how
// onError knows that listener will send a normalized copy and the native
// InvalidError report is a duplicate. Pages with no Vue app never set it, so
// their native reports still go out.
let hasVueUnhandledRejectionListener = false

/**
 * Marks that createVueApp.ts's unhandledrejection listener is active on this page.
 */
export const markVueUnhandledRejectionListenerActive = (): void => {
	hasVueUnhandledRejectionListener = true
}

/**
 * Determines whether an InvalidError report duplicates the normalized Error
 * createVueApp.ts's unhandledrejection listener sends for the same rejection.
 * @param errorClass - The reported error's class name
 * @param hasUnhandledRejectionMetadata - Whether the event carries Bugsnag's own
 * "unhandledrejection handler" metadata tab
 * @param vueListenerActive - Whether createVueApp.ts's unhandledrejection listener has
 * been attached on this page
 * @returns true when the report is a duplicate that should be suppressed
 */
export const isDuplicateVueRejectionReport = (
	errorClass: string | undefined,
	hasUnhandledRejectionMetadata: boolean,
	vueListenerActive: boolean
): boolean =>
	errorClass === 'InvalidError' && vueListenerActive && hasUnhandledRejectionMetadata

/**
 * Bugsnag's onError callback: drops noise and regroups inline-script errors.
 * @param event - The event Bugsnag is about to report
 * @returns false to suppress the report, true to send it
 */
export const handleBugsnagError = (event: Event): boolean => {
	const [firstError] = event.errors
	const errorClass = firstError?.errorClass
	const errorMessage = firstError?.errorMessage ?? ''
	const stacktrace = firstError?.stacktrace ?? []
	const nonErrorPayload: unknown =
		event.getMetadata('window onerror', 'non-error parameter') ??
		event.getMetadata('unhandledrejection handler', 'non-error parameter')

	if (
		isBraveIosInjectedScriptError(errorMessage) ||
		isCefSharpBotError(nonErrorPayload) ||
		isCspEvalViolationError(errorMessage) ||
		isDuplicateVueRejectionReport(
			errorClass,
			event.getMetadata('unhandledrejection handler') !== undefined,
			hasVueUnhandledRejectionListener
		) ||
		isFailedFetchError(errorMessage) ||
		isResizeObserverLoopError(errorMessage) ||
		isThirdPartyScriptError(stacktrace) ||
		isUntraceableScriptError(stacktrace) ||
		isVideoLibraryDecodeError(errorClass, event.context, nonErrorPayload) ||
		isZendeskWidgetError(errorMessage)
	) {
		return false
	}

	// Without a filename, Bugsnag groups on the HTML line number, so one crash
	// becomes a new group on every deploy. Group on class and message instead.
	if (isInlineDocumentError(stacktrace, errorClass)) {
		const locationFrame = stacktrace.find(frame => frame.file)

		event.groupingHash =
			`inline-document:${errorClass ?? 'Error'}:${errorMessage}`
		event.addMetadata('inlineScript', {
			column: locationFrame?.columnNumber,
			file: locationFrame?.file,
			line: locationFrame?.lineNumber,
		})
	}

	return true
}

if (bugsnagApiKey && typeof window !== 'undefined') {
	const currentUser: Partial<CurrentUser> | undefined = window.current_user

	Bugsnag.start({
		apiKey: bugsnagApiKey,
		appVersion: import.meta.env.APP_VERSION || 'unknown',
		onBreadcrumb(breadcrumb) {
			return !isNoisyRequestBreadcrumb(breadcrumb)
		},
		onError: handleBugsnagError,
		plugins: [new BugsnagPluginVue()],
		user: {
			email: currentUser?.email,
			id: currentUser?.id?.toString(),
			name: currentUser?.name,
		},
	})
}

/**
 * Attaches Bugsnag to a Vue 3 application
 * @param app - Vue 3 application instance
 */
export default function (app: App) {
	if (!bugsnagApiKey || typeof window === 'undefined') {
		return
	}

	try {
		const bugsnagVue = Bugsnag.getPlugin('vue')

		if (bugsnagVue) {
			app.use(bugsnagVue)
		}
	} catch (error) {
		if (import.meta.env.DEV) {
			console.error('Failed to initialize Bugsnag Vue plugin:', error)
		}
	}
}
